import { useEffect, useMemo, useRef } from "react";
import { MAX_QUANTITY_GRAM, MAX_SPICES } from "../../data/schema";
import { useCache } from "../useCache";
import { useFilterSpices } from "../useFilter";
import { useTexture } from "@react-three/drei";
import { NearestFilter, NoColorSpace, RepeatWrapping } from "three";
import { RESOURCES } from "@/data/resources";
import {useControls, folder} from 'leva'
import { useDebug } from "../useDebug";

type CustomText = {
  data: import("three").DataArrayTexture;
  layers: Map<
    number,
    {
      id: number;
      texSlot: number;
      data: Uint8Array | null;
      needsUpdate: boolean;
    }
  >;
};

export type useUniformsProps = {
  texture: CustomText;
  normal: CustomText;
  fallback: CustomText;
};

export function useUniforms({ texture, normal, fallback }: useUniformsProps) {
  const [spices] = useFilterSpices();
  const { data } = useCache();

  const noiseMap = useTexture(RESOURCES.noise.path);

  // Set wrapping only once when texture loads
  useEffect(() => {
    if (noiseMap) {
      // noiseMap.generateMipmaps = false;
      noiseMap.minFilter = NearestFilter; 
      noiseMap.magFilter = NearestFilter;
      noiseMap.colorSpace = NoColorSpace; // keep it as data
      noiseMap.wrapS = RepeatWrapping;
      noiseMap.wrapT = RepeatWrapping;
      noiseMap.needsUpdate = true;      
    }
  }, [noiseMap]);

  // Load noise texture once
  const uniforms = useRef({

    uActiveCount: { value: 0 },
    uActiveTextureSlots: { value: new Array(MAX_SPICES).fill(0) },
    uTexture: { value: texture.data },
    uNormal: { value: normal.data },
    uFallback: { value: fallback.data },
    uColorIntensity: { value: new Array(MAX_SPICES).fill(1) },
    uTextureScale: { value: new Array(MAX_SPICES).fill(0) },
    uNorIntensity: { value: new Array(MAX_SPICES).fill(0) },
    uRoughnessIntensity: { value: new Array(MAX_SPICES).fill(0) },
    uAoIntensity: { value: new Array(MAX_SPICES).fill(0) },
    uHighResMapsReady: { value: new Array(MAX_SPICES).fill(false) },

    // Stacking..
    uQuantity: { value: new Array(MAX_SPICES).fill(0) },
    uAccQuantities: { value: 0 },

    // Noise
    uNoiseMap: { value: noiseMap },
    uNoiseScale: {value: 0.5}, 
    uNoiseAmp: {value: 0.15}, 
    uNoiseSeed: {value: 0}, 

    // per-layer mask noise
    uMaskSeeds: { value: new Array(MAX_SPICES).fill(0) },

    // Update 
  });

  // Memoize texture readiness sets to prevent recreating on every render
  const lowResReady = useMemo(() =>
    new Set([...fallback.layers.values()].filter(l => l.data).map(l => l.id)),
    [fallback.layers]
  );

  const highResReady = useMemo(() =>
    new Set([...texture.layers.values(), ...normal.layers.values()].filter(l => l.data).map(l => l.id)),
    [texture.layers, normal.layers]
  );

  const needsUpdate = useMemo(() =>
    new Set([...fallback.layers.values(), ...texture.layers.values(), ...normal.layers.values()].filter(l => l.needsUpdate).map(l => l.id)),
    [fallback.layers, texture.layers, normal.layers]
  );

  /** Effect A — Quantity updates (runs on every spices change — expected and cheap) */
  useEffect(() => {
    const quantities = new Array(MAX_SPICES).fill(0);

    Array.from(spices.entries()).forEach(([id, spice], index) => {
      if (index >= MAX_SPICES) return;

      if (lowResReady.has(id)) {
        quantities[index] = spice.quantity / MAX_QUANTITY_GRAM;
      }

      if (uniforms.current.uQuantity.value[index] !== (spice.quantity / MAX_QUANTITY_GRAM)) {
        uniforms.current.uMaskSeeds.value[index] = Math.random();
      }
    });

    uniforms.current.uQuantity.value = quantities;
    uniforms.current.uAccQuantities.value = quantities.reduce((acc, curr) => acc + curr, 0);
    uniforms.current.uActiveCount.value = lowResReady.size;
  }, [spices, lowResReady]);

  /** Effect B — Texture props update (runs only when textures change) */
  useEffect(() => {
    if (!data) return;

    Array.from(spices.entries()).forEach(([id], index) => {
      if (index >= MAX_SPICES) return;

      const fallbackLayer = fallback.layers.get(id);
      const spiceData = data[id as keyof typeof data];

      if (needsUpdate.has(id) && fallbackLayer && spiceData) {
        uniforms.current.uActiveTextureSlots.value[index] = fallbackLayer.texSlot;
        uniforms.current.uTextureScale.value[index] = spiceData.shaderprops.scale ?? 1;
        uniforms.current.uNorIntensity.value[index] = spiceData.shaderprops.norIntensity ?? 1;
        uniforms.current.uRoughnessIntensity.value[index] = spiceData.shaderprops.roughnessIntensity ?? 1;
        if (fallbackLayer.texSlot !== undefined) {
          uniforms.current.uColorIntensity.value[fallbackLayer.texSlot] = spiceData.shaderprops.colorIntensity ?? 1;   //prettier-ignore
          uniforms.current.uAoIntensity.value[fallbackLayer.texSlot] = spiceData.shaderprops.aoIntensity ?? 1;         //prettier-ignore
        }
      }

      if (highResReady.has(id)) {
        const texLayer = texture.layers.get(id);
        const normLayer = normal.layers.get(id);
        uniforms.current.uHighResMapsReady.value[index] = !!(texLayer?.data && normLayer?.data);
      }
    });
  }, [data, spices, texture.layers, normal.layers, fallback.layers, needsUpdate, highResReady]);

  // debug
  const isDebug = useDebug();
  const controlsConfig = useMemo(() => {
    if (!isDebug || !spices || !data || !uniforms) return {};
    return Object.fromEntries(
      Array.from(spices.entries())
        .filter(([key]) => data[key as keyof typeof data] && fallback.layers.get(key))
        .map(([key, spice]) => {
          const spiceData = data[key as keyof typeof data]!;
          const layer = fallback.layers.get(key)!;
          return [
            spice.name,
            folder({
              [`ColorIntensity-${spice.name}`]: {
                value: spiceData.shaderprops.colorIntensity,
                min: 0.1,
                max: 5,
                step: 0.01,
                onChange: (v: number) => {
                  uniforms.current.uColorIntensity.value[layer.texSlot] = v;
                },
              },
              [`TextureScale-${spice.name}`]: {
                value: spiceData.shaderprops.scale,
                min: 0.1,
                max: 10,
                step: 0.01,
                onChange: (v: number) => {
                  uniforms.current.uTextureScale.value[layer.texSlot] = v;
                },
              },
              [`NorIntensity-${spice.name}`]: {
                value: spiceData.shaderprops.norIntensity,
                min: 0,
                max: 10,
                step: 0.01,
                onChange: (v: number) => {
                  uniforms.current.uNorIntensity.value[layer.texSlot] = v;
                },
              },
              [`RoughnessIntensity-${spice.name}`]: {
                value: spiceData.shaderprops.roughnessIntensity,
                min: 0,
                max: 2,
                step: 0.01,
                onChange: (v: number) => {
                  uniforms.current.uRoughnessIntensity.value[layer.texSlot] = v;
                },
              },
              [`AoIntensity-${spice.name}`]: {
                value: spiceData.shaderprops.aoIntensity,
                min: 0,
                max: 2,
                step: 0.01,
                onChange: (v: number) => {
                  uniforms.current.uAoIntensity.value[layer.texSlot] = v;
                },
              },
            }),
          ];
        })
    );
  }, [isDebug, spices, data, fallback, uniforms]);
  useControls("Spices", controlsConfig, { collapsed: true }, [controlsConfig]);

  return uniforms.current;
}
